import React, { Suspense, lazy } from 'react';
import { BrowserRouter, HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AppProvider, useApp } from './context';
import { MainLayout } from './components/layout';
import { usePermissions } from './hooks/usePermissions';
import { homeRoute } from './utils/rbac';
import { canEditRecovery } from './utils/qcAccess';
import { AccessDenied, Notifications } from './components/common';
import { PageErrorBoundary } from './components/common/ErrorBoundary';

// Eagerly loaded (always needed)
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';

// Lazy-loaded pages – each module becomes its own chunk
const ApprovalQueue = lazy(() => import('./pages/ApprovalQueue'));
const NotificationCenter = lazy(() => import('./pages/NotificationCenter'));

// Finance
const ChartOfAccounts = lazy(() => import('./pages/finance/ChartOfAccounts').then(m => ({ default: m.ChartOfAccounts })));
const JournalEntries = lazy(() => import('./pages/finance/JournalEntries').then(m => ({ default: m.JournalEntries })));
const BankReconciliation = lazy(() => import('./pages/finance/BankReconciliation'));
const CreditLimitManagement = lazy(() => import('./pages/finance/CreditLimitManagement'));
const GeneralLedger = lazy(() => import('./pages/finance/GeneralLedger').then(m => ({ default: m.GeneralLedger })));
const TrialBalance = lazy(() => import('./pages/finance/TrialBalance').then(m => ({ default: m.TrialBalance })));
const ProfitAndLoss = lazy(() => import('./pages/finance/ProfitAndLoss').then(m => ({ default: m.ProfitAndLoss })));
const BalanceSheet = lazy(() => import('./pages/finance/BalanceSheet').then(m => ({ default: m.BalanceSheet })));

// Inventory
const Items = lazy(() => import('./pages/inventory/Items').then(m => ({ default: m.Items })));
const Warehouses = lazy(() => import('./pages/inventory/Warehouses').then(m => ({ default: m.Warehouses })));
const GatePassManagement = lazy(() => import('./pages/inventory/GatePassManagement'));
const StockLedger = lazy(() => import('./pages/inventory/StockLedger'));
const StockMovements = lazy(() => import('./pages/inventory/StockMovements').then(m => ({ default: m.StockMovements })));
const StockValuation = lazy(() => import('./pages/inventory/StockValuation'));
const LowStockAlerts = lazy(() => import('./pages/inventory/LowStockAlerts'));

// Sales
const Customers = lazy(() => import('./pages/sales/Customers').then(m => ({ default: m.Customers })));
const SalesInvoices = lazy(() => import('./pages/sales/SalesInvoices').then(m => ({ default: m.SalesInvoices })));
const POSTerminal = lazy(() => import('./pages/sales/POSTerminal'));
const SalesOrders = lazy(() => import('./pages/sales/SalesOrders'));
const CustomerReceipts = lazy(() => import('./pages/sales/CustomerReceipts'));
const AgingReport = lazy(() => import('./pages/sales/AgingReport'));

// Purchase module (src/modules/purchase — hub + structure)
const PurchaseHub = lazy(() => import('../modules/purchase/pages/PurchaseHub'));
const Vendors = lazy(() => import('../modules/purchase/pages/masters/VendorList'));
const VendorForm = lazy(() => import('../modules/purchase/pages/masters/VendorForm'));
const MaterialList = lazy(() => import('../modules/purchase/pages/masters/MaterialList'));
const MaterialForm = lazy(() => import('../modules/purchase/pages/masters/MaterialForm'));
const AvailableMaterialList = lazy(() => import('../modules/purchase/pages/masters/AvailableMaterialList'));
const PayableVendorList = lazy(() => import('../modules/purchase/pages/masters/PayableVendorList'));
const PurchaseOrgList = lazy(() => import('../modules/purchase/pages/masters/PurchaseOrgList'));
const PurchaseGroupList = lazy(() => import('../modules/purchase/pages/masters/PurchaseGroupList'));
const PRDataList = lazy(() => import('../modules/purchase/pages/masters/PRDataList'));
const RFQDataList = lazy(() => import('../modules/purchase/pages/masters/RFQDataList'));
const PODataList = lazy(() => import('../modules/purchase/pages/masters/PODataList'));
const PRForm = lazy(() => import('../modules/purchase/pages/requisition/PRForm'));
const PRList = lazy(() => import('../modules/purchase/pages/requisition/PRList'));
const PRApproval = lazy(() => import('../modules/purchase/pages/requisition/PRApproval'));
const RFQPage = lazy(() => import('../modules/purchase/pages/rfq/RFQPage'));
const CriteriaPage = lazy(() => import('../modules/purchase/pages/rfq/CriteriaPage'));
const QuotationPage = lazy(() => import('../modules/purchase/pages/rfq/QuotationPage'));
const ComparisonPage = lazy(() => import('../modules/purchase/pages/rfq/ComparisonPage'));
const RFQPRPage = lazy(() => import('../modules/purchase/pages/rfq/RFQPRPage'));
const RFQList = lazy(() => import('../modules/purchase/pages/rfq/RFQList'));
const QuotationComparison = lazy(() => import('../modules/purchase/pages/rfq/QuotationComparison'));
const POForm = lazy(() => import('../modules/purchase/pages/order/POForm'));
const PurchaseOrders = lazy(() => import('../modules/purchase/pages/order/POList'));
const POApproval = lazy(() => import('../modules/purchase/pages/order/POApproval'));
const GRNForm = lazy(() => import('../modules/purchase/pages/grn/GRNForm'));
const ReturnDelivery = lazy(() => import('../modules/purchase/pages/grn/ReturnDelivery'));
const GoodsReceiptNote = lazy(() => import('../modules/purchase/pages/grn/GRNList'));
const InvoiceForm = lazy(() => import('../modules/purchase/pages/invoice/InvoiceForm'));
const PurchaseInvoices = lazy(() => import('../modules/purchase/pages/invoice/InvoiceList'));
const PaymentForm = lazy(() => import('../modules/purchase/pages/payment/PaymentForm'));
const VendorPayments = lazy(() => import('../modules/purchase/pages/payment/PaymentList'));

// Manufacturing
const BillOfMaterials = lazy(() => import('./pages/manufacturing/BillOfMaterials'));
const ProductionOrders = lazy(() => import('./pages/manufacturing/ProductionOrders'));
const ShopFloorKiosk = lazy(() => import('./pages/manufacturing/ShopFloorKiosk'));
const OEEDashboard = lazy(() => import('./pages/manufacturing/OEEDashboard'));
const MRPPlanning = lazy(() => import('./pages/manufacturing/MRPPlanning'));
const JobWorkChallan = lazy(() => import('./pages/manufacturing/JobWorkChallan'));
const WorkCenters = lazy(() => import('./pages/manufacturing/WorkCenters'));
const QCInspection = lazy(() => import('./pages/manufacturing/QCInspection'));
const RawMaterialInspection = lazy(() => import('./pages/manufacturing/RawMaterialInspection'));
const ProductInspection = lazy(() => import('./pages/manufacturing/ProductInspection'));
const RecoveryForm = lazy(() => import('./pages/manufacturing/RecoveryForm'));

// HRM
const Employees = lazy(() => import('./pages/hrm/Employees'));
const Payroll = lazy(() => import('./pages/hrm/Payroll'));
const Attendance = lazy(() => import('./pages/hrm/Attendance'));
const LeaveManagement = lazy(() => import('./pages/hrm/LeaveManagement'));
const TaxDeclarations = lazy(() => import('./pages/hrm/TaxDeclarations'));

// Reports
const FinancialReports = lazy(() => import('./pages/reports/FinancialReports'));
const SalesReports = lazy(() => import('./pages/reports/SalesReports'));
const PurchaseReports = lazy(() => import('./pages/reports/PurchaseReports'));
const InventoryReports = lazy(() => import('./pages/reports/InventoryReports'));
const GSTReports = lazy(() => import('./pages/reports/GSTReports'));
const ProductionReports = lazy(() => import('./pages/reports/ProductionReports'));
const RecoveryReportAdmin = lazy(() => import('./pages/reports/RecoveryReport'));

// GST
const EInvoice = lazy(() => import('./pages/gst/EInvoice').then(m => ({ default: m.EInvoice })));
const GSTR1 = lazy(() => import('./pages/gst/GSTR1').then(m => ({ default: m.GSTR1 })));
const TdsManagement = lazy(() => import('./pages/gst/TdsManagement'));
const GSTR2BReconciliation = lazy(() => import('./pages/gst/GSTR2BReconciliation'));
const EWayBill = lazy(() => import('./pages/gst/EWayBill'));
const ITCReconciliation = lazy(() => import('./pages/gst/ITCReconciliation'));
const HSNSummary = lazy(() => import('./pages/gst/HSNSummary'));

// Settings
const Settings = lazy(() => import('./pages/settings/Settings').then(m => ({ default: m.Settings })));
const ChangePassword = lazy(() => import('./pages/settings/ChangePassword'));
const MovementTypes = lazy(() => import('./pages/settings/MovementTypes'));

// Dairy
const DairyDashboard = lazy(() => import('./pages/dairy/DairyDashboard'));
const DairySuppliers = lazy(() => import('./pages/dairy/Suppliers'));
const DairyCentersRoutes = lazy(() => import('./pages/dairy/CentersRoutes'));
const DairyRateChart = lazy(() => import('./pages/dairy/RateChart'));
const DairyPurchasePayments = lazy(() => import('./pages/dairy/PurchasePayments'));
const DairyProcessing = lazy(() => import('./pages/dairy/Processing'));
const DairyPacking = lazy(() => import('./pages/dairy/Packing'));

function LoadingScreen() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-surface-50 dark:bg-surface-900">
      <div className="h-12 w-12 rounded-xl bg-primary-600 flex items-center justify-center mb-4 animate-pulse-soft">
        <span className="text-white font-bold text-lg">DM</span>
      </div>
      <div className="h-1 w-32 bg-surface-200 dark:bg-surface-700 rounded-full overflow-hidden">
        <div className="h-full w-1/2 bg-primary-500 rounded-full animate-shimmer" />
      </div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { state } = useApp();
  
  if (state.isLoading) return <LoadingScreen />;
  if (!state.isAuthenticated) return <Navigate to="/login" replace />;
  
  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { state } = useApp();
  
  if (state.isLoading) return <LoadingScreen />;
  if (state.isAuthenticated) {
    let roles: string[] = [];
    try {
      const parsed = JSON.parse(state.user?.roles || '[]');
      roles = Array.isArray(parsed) ? parsed : [state.user?.roles || ''];
    } catch {
      roles = state.user?.roles ? [state.user.roles] : [];
    }
    return <Navigate to={homeRoute(roles)} replace />;
  }
  
  return <>{children}</>;
}

/** Wraps a route element with permission check */
function Guarded({ children, module, adminOnly }: { children: React.ReactNode; module?: string; adminOnly?: boolean }) {
  const { canRoute, userRoles } = usePermissions();
  const location = useLocation();

  if (adminOnly && !userRoles.includes('admin')) {
    return <AccessDenied />;
  }
  if (location.pathname === '/manufacturing/qc/recovery' && !canEditRecovery(userRoles)) {
    return <AccessDenied />;
  }
  if (!canRoute(location.pathname)) {
    return <AccessDenied />;
  }

  return (
    <PageErrorBoundary moduleName={module}>
      <Suspense fallback={<ChunkLoading />}>
        {children}
      </Suspense>
    </PageErrorBoundary>
  );
}

function ChunkLoading() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="h-8 w-8 rounded-lg bg-primary-600 animate-pulse-soft" />
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      
      {/* Protected Routes */}
      <Route
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Guarded module="Dashboard"><Dashboard /></Guarded>} />
        <Route path="/approvals" element={<Guarded module="Approvals"><ApprovalQueue /></Guarded>} />
        <Route path="/notifications" element={<Guarded module="Notifications"><NotificationCenter /></Guarded>} />
        
        {/* Finance Module */}
        <Route path="/finance" element={<Guarded module="Finance"><ChartOfAccounts /></Guarded>} />
        <Route path="/finance/accounts" element={<Guarded module="Finance"><ChartOfAccounts /></Guarded>} />
        <Route path="/finance/journal" element={<Guarded module="Finance"><JournalEntries /></Guarded>} />
        <Route path="/finance/bank-reconciliation" element={<Guarded module="Finance"><BankReconciliation /></Guarded>} />
        <Route path="/finance/credit-management" element={<Guarded module="Finance"><CreditLimitManagement /></Guarded>} />
        <Route path="/finance/ledger" element={<Guarded module="Finance"><GeneralLedger /></Guarded>} />
        <Route path="/finance/trial-balance" element={<Guarded module="Finance"><TrialBalance /></Guarded>} />
        <Route path="/finance/pnl" element={<Guarded module="Finance"><ProfitAndLoss /></Guarded>} />
        <Route path="/finance/balance-sheet" element={<Guarded module="Finance"><BalanceSheet /></Guarded>} />
        
        {/* Inventory Module */}
        <Route path="/inventory" element={<Guarded module="Inventory"><Items /></Guarded>} />
        <Route path="/inventory/items" element={<Guarded module="Inventory"><Items /></Guarded>} />
        <Route path="/inventory/warehouses" element={<Guarded module="Inventory"><Warehouses /></Guarded>} />
        <Route path="/inventory/stock-ledger" element={<Guarded module="Inventory"><StockLedger /></Guarded>} />
        <Route path="/inventory/gate-pass" element={<Guarded module="Inventory"><GatePassManagement /></Guarded>} />
        <Route path="/inventory/moves" element={<Guarded module="Inventory"><StockMovements /></Guarded>} />
        <Route path="/inventory/valuation" element={<Guarded module="Inventory"><StockValuation /></Guarded>} />
        <Route path="/inventory/low-stock" element={<Guarded module="Inventory"><LowStockAlerts /></Guarded>} />
        
        {/* Sales Module */}
        <Route path="/sales" element={<Guarded module="Sales"><SalesInvoices /></Guarded>} />
        <Route path="/sales/customers" element={<Guarded module="Sales"><Customers /></Guarded>} />
        <Route path="/sales/orders" element={<Guarded module="Sales"><SalesOrders /></Guarded>} />
        <Route path="/sales/invoices" element={<Guarded module="Sales"><SalesInvoices /></Guarded>} />
        <Route path="/sales/pos" element={<Guarded module="Sales"><POSTerminal /></Guarded>} />
        <Route path="/sales/receipts" element={<Guarded module="Sales"><CustomerReceipts /></Guarded>} />
        <Route path="/sales/aging" element={<Guarded module="Sales"><AgingReport /></Guarded>} />
        
        {/* Purchase module — hub + milk payments in sidebar; full map on /purchase */}
        <Route path="/purchase" element={<Guarded module="Purchase"><PurchaseHub /></Guarded>} />
        <Route path="/purchase/vendors" element={<Guarded module="Purchase"><Vendors /></Guarded>} />
        <Route path="/purchase/vendors/new" element={<Guarded module="Purchase"><VendorForm /></Guarded>} />
        <Route path="/purchase/vendors/edit/:id" element={<Guarded module="Purchase"><VendorForm /></Guarded>} />
        <Route path="/purchase/vendors/payable" element={<Guarded module="Purchase"><PayableVendorList /></Guarded>} />
        <Route path="/purchase/orgs" element={<Guarded module="Purchase"><PurchaseOrgList /></Guarded>} />
        <Route path="/purchase/groups" element={<Guarded module="Purchase"><PurchaseGroupList /></Guarded>} />
        <Route path="/purchase/materials" element={<Guarded module="Purchase"><MaterialList /></Guarded>} />
        <Route path="/purchase/materials/new" element={<Guarded module="Purchase"><MaterialForm /></Guarded>} />
        <Route path="/purchase/materials/available" element={<Guarded module="Purchase"><AvailableMaterialList /></Guarded>} />
        <Route path="/purchase/master/pr-data" element={<Guarded module="Purchase"><PRDataList /></Guarded>} />
        <Route path="/purchase/master/rfq-data" element={<Guarded module="Purchase"><RFQDataList /></Guarded>} />
        <Route path="/purchase/master/po-data" element={<Guarded module="Purchase"><PODataList /></Guarded>} />
        <Route path="/purchase/pr/new" element={<Guarded module="Purchase"><PRForm /></Guarded>} />
        <Route path="/purchase/pr" element={<Guarded module="Purchase"><PRList /></Guarded>} />
        <Route path="/purchase/pr/approval" element={<Guarded module="Purchase"><PRApproval /></Guarded>} />
        <Route path="/purchase/rfq/new" element={<Guarded module="Purchase"><RFQPage /></Guarded>} />
        <Route path="/purchase/rfq/:id" element={<Guarded module="Purchase"><RFQPage /></Guarded>} />
        <Route path="/purchase/rfq/criteria/:id?" element={<Guarded module="Purchase"><CriteriaPage /></Guarded>} />
        <Route path="/purchase/rfq/quotation/:id?" element={<Guarded module="Purchase"><QuotationPage /></Guarded>} />
        <Route path="/purchase/rfq/comparison/:id?" element={<Guarded module="Purchase"><ComparisonPage /></Guarded>} />
        <Route path="/purchase/rfq/rfqpr/:id?" element={<Guarded module="Purchase"><RFQPRPage /></Guarded>} />
        <Route path="/purchase/rfq" element={<Guarded module="Purchase"><RFQList /></Guarded>} />
        <Route path="/purchase/rfq/compare" element={<Guarded module="Purchase"><QuotationComparison /></Guarded>} />
        <Route path="/purchase/orders/new" element={<Guarded module="Purchase"><POForm /></Guarded>} />
        <Route path="/purchase/orders" element={<Guarded module="Purchase"><PurchaseOrders /></Guarded>} />
        <Route path="/purchase/orders/approval" element={<Guarded module="Purchase"><POApproval /></Guarded>} />
        <Route path="/purchase/grn/new" element={<Guarded module="Purchase"><GRNForm /></Guarded>} />
        <Route path="/purchase/grn/return" element={<Guarded module="Purchase"><ReturnDelivery /></Guarded>} />
        <Route path="/purchase/grn" element={<Guarded module="Purchase"><GoodsReceiptNote /></Guarded>} />
        <Route path="/purchase/invoices/new" element={<Guarded module="Purchase"><InvoiceForm /></Guarded>} />
        <Route path="/purchase/invoices" element={<Guarded module="Purchase"><PurchaseInvoices /></Guarded>} />
        <Route path="/purchase/payments/new" element={<Guarded module="Purchase"><PaymentForm /></Guarded>} />
        <Route path="/purchase/payments" element={<Guarded module="Purchase"><VendorPayments /></Guarded>} />
        <Route path="/purchase/milk-payments" element={<Guarded module="Purchase"><DairyPurchasePayments /></Guarded>} />
        
        {/* Manufacturing Module */}
        <Route path="/manufacturing" element={<Guarded module="Manufacturing"><ProductionOrders /></Guarded>} />
        <Route path="/manufacturing/bom" element={<Guarded module="Manufacturing"><BillOfMaterials /></Guarded>} />
        <Route path="/manufacturing/work-centers" element={<Guarded module="Manufacturing"><WorkCenters /></Guarded>} />
        <Route path="/manufacturing/production" element={<Guarded module="Manufacturing"><ProductionOrders /></Guarded>} />
        <Route path="/manufacturing/mrp" element={<Guarded module="Manufacturing"><MRPPlanning /></Guarded>} />
        <Route path="/manufacturing/job-work" element={<Guarded module="Manufacturing"><JobWorkChallan /></Guarded>} />
        <Route path="/manufacturing/shop-floor" element={<Guarded module="Manufacturing"><ShopFloorKiosk /></Guarded>} />
        <Route path="/manufacturing/oee" element={<Guarded module="Manufacturing"><OEEDashboard /></Guarded>} />
        <Route path="/manufacturing/qc" element={<Guarded module="Manufacturing"><QCInspection /></Guarded>} />
        <Route path="/manufacturing/qc/raw-material" element={<Guarded module="Manufacturing"><RawMaterialInspection /></Guarded>} />
        <Route path="/manufacturing/qc/product" element={<Guarded module="Manufacturing"><ProductInspection /></Guarded>} />
        <Route path="/manufacturing/qc/recovery" element={<Guarded module="Manufacturing"><RecoveryForm /></Guarded>} />
        
        {/* HRM Module */}
        <Route path="/hrm" element={<Guarded module="HRM"><Employees /></Guarded>} />
        <Route path="/hrm/employees" element={<Guarded module="HRM"><Employees /></Guarded>} />
        <Route path="/hrm/attendance" element={<Guarded module="HRM"><Attendance /></Guarded>} />
        <Route path="/hrm/leave" element={<Guarded module="HRM"><LeaveManagement /></Guarded>} />
        <Route path="/hrm/payroll" element={<Guarded module="HRM"><Payroll /></Guarded>} />
        <Route path="/hrm/tax" element={<Guarded module="HRM"><TaxDeclarations /></Guarded>} />
        
        {/* GST Module */}
        <Route path="/gst" element={<Guarded module="GST"><EInvoice /></Guarded>} />
        <Route path="/gst/e-invoice" element={<Guarded module="GST"><EInvoice /></Guarded>} />
        <Route path="/gst/e-way-bill" element={<Guarded module="GST"><EWayBill /></Guarded>} />
        <Route path="/gst/gstr1" element={<Guarded module="GST"><GSTR1 /></Guarded>} />
        <Route path="/gst/gstr2b" element={<Guarded module="GST"><GSTR2BReconciliation /></Guarded>} />
        <Route path="/gst/tds" element={<Guarded module="GST"><TdsManagement /></Guarded>} />
        <Route path="/gst/itc" element={<Guarded module="GST"><ITCReconciliation /></Guarded>} />
        <Route path="/gst/hsn" element={<Guarded module="GST"><HSNSummary /></Guarded>} />
        
        {/* Reports Module */}
        <Route path="/reports" element={<Guarded module="Reports"><FinancialReports /></Guarded>} />
        <Route path="/reports/financial" element={<Guarded module="Reports"><FinancialReports /></Guarded>} />
        <Route path="/reports/sales" element={<Guarded module="Reports"><SalesReports /></Guarded>} />
        <Route path="/reports/purchase" element={<Guarded module="Reports"><PurchaseReports /></Guarded>} />
        <Route path="/reports/inventory" element={<Guarded module="Reports"><InventoryReports /></Guarded>} />
        <Route path="/reports/gst" element={<Guarded module="Reports"><GSTReports /></Guarded>} />
        <Route path="/reports/production" element={<Guarded module="Reports"><ProductionReports /></Guarded>} />
        <Route path="/reports/recovery" element={<Guarded module="Reports" adminOnly><RecoveryReportAdmin /></Guarded>} />
        
        {/* Dairy Module */}
        <Route path="/dairy" element={<Guarded module="Dairy"><DairyDashboard /></Guarded>} />
        <Route path="/dairy/dashboard" element={<Guarded module="Dairy"><DairyDashboard /></Guarded>} />
        <Route path="/dairy/suppliers" element={<Guarded module="Dairy"><DairySuppliers /></Guarded>} />
        <Route path="/dairy/farmers" element={<Navigate to="/dairy/suppliers" replace />} />
        <Route path="/dairy/centers" element={<Guarded module="Dairy"><DairyCentersRoutes /></Guarded>} />
        <Route path="/dairy/rate-chart" element={<Guarded module="Dairy"><DairyRateChart /></Guarded>} />
        <Route path="/dairy/purchase-payments" element={<Navigate to="/purchase/milk-payments" replace />} />
        <Route path="/dairy/processing" element={<Guarded module="Dairy"><DairyProcessing /></Guarded>} />
        <Route path="/dairy/packing" element={<Guarded module="Dairy"><DairyPacking /></Guarded>} />

        {/* Settings Module */}
        <Route path="/account/password" element={<Guarded><ChangePassword /></Guarded>} />
        <Route path="/settings" element={<Guarded module="Settings"><Settings /></Guarded>} />
        <Route path="/settings/company" element={<Guarded module="Settings"><Settings /></Guarded>} />
        <Route path="/settings/users" element={<Guarded module="Settings"><Settings /></Guarded>} />
        <Route path="/settings/roles" element={<Guarded module="Settings"><Settings /></Guarded>} />
        <Route path="/settings/master" element={<Guarded module="Settings"><Settings /></Guarded>} />
        <Route path="/settings/movement-types" element={<Guarded module="Settings"><MovementTypes /></Guarded>} />
      </Route>
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AppRouter({ children }: { children: React.ReactNode }) {
  const useHashRouter = import.meta.env.VITE_ROUTER_MODE === 'hash';
  return useHashRouter ? <HashRouter>{children}</HashRouter> : <BrowserRouter>{children}</BrowserRouter>;
}

export function App() {
  return (
    <AppProvider>
      <Notifications />
      <AppRouter>
        <AppRoutes />
      </AppRouter>
    </AppProvider>
  );
}

export default App;
